import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Tabs,
    Tab,
    Box,
    IconButton,
    Typography,
    Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { TestingStep, PromptVersion } from './types';
import { PromptEditor } from './PromptEditor';
import { PromptPreview } from './PromptPreview';
import { PromptTesting } from './PromptTesting';
import { ConfirmDialog } from './components/ConfirmDialog';
import { OverlayLoader } from 'components/Common/Loader/loaderWithOverlay';

import { toast } from 'react-toastify';
import { aiMatchingService, ApplyPromptToAllResponse } from 'services/aiMatchingService';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { useJobPipeline } from 'containers/JobPipeline/contexts/JobPipelineContext';

interface AIMatchingSettingsPanelProps {
    open: boolean;
    onClose: () => void;
    job: {
        jobId: string;
        jobTitle: string;
    };
}

export const AIMatchingSettingsPanel: React.FC<AIMatchingSettingsPanelProps> = ({
    open,
    onClose,
    job,
}) => {
    const [activeTab, setActiveTab] = useState<TestingStep>('refine');
    const [draftPrompt, setDraftPrompt] = useState('');
    const [versions, setVersions] = useState<PromptVersion[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState<string>('');
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [versionToDelete, setVersionToDelete] = useState<string | null>(null);
    const [applyToAllVersionId, setApplyToAllVersionId] = useState<string | null>(null);
    const expertId = useSelector((state: RootState) => state.auth.user.expertId);

    const fetchVersions = async () => {
        setIsLoading(true);
        try {
            const response = await aiMatchingService.getPromptVersions({
                expertId,
                jobId: job.jobId,
            });

            if (response.apiStatus === 'SUCCESS' && response.output.versions) {
                // Transform API response to match our PromptVersion interface
                const transformedVersions: PromptVersion[] = response.output.versions.map((v: {
                    versionId: string;
                    version: number;
                    prompt: { system: string; userTemplate: string };
                    status: string;
                    isActive: boolean;
                    createdAt: number;
                    changelog: string;
                    experimentName?: string;
                    parentActiveVersionId?: string;
                    candidateEvaluations?: any[];
                }) => ({
                    id: v.versionId,
                    jobId: response.output.jobId,
                    version: v.version,
                    prompt: v.prompt,
                    createdAt: new Date(v.createdAt).toISOString(),
                    createdBy: 'System', // Can be enhanced based on backend data
                    isActive: v.isActive,
                    notes: v.changelog,
                    status: v.status,
                    experimentName: v.experimentName,
                    parentActiveVersionId: v.parentActiveVersionId,
                    evaluations: v.candidateEvaluations?.map(e => ({
                        candidateId: e.candidateId,
                        score: parseFloat(e.evaluationResult?.final_score || '0') / 100,
                        reasoning: e.evaluationResult?.recommendation || 'No reasoning provided.',
                        matchStrengths: e.evaluationResult?.match_strengths,
                        matchGaps: e.evaluationResult?.match_gaps,
                        recommendation: e.evaluationResult?.recommendation,
                        growthPotential: e.evaluationResult?.growth_potential,
                        riskFactors: e.evaluationResult?.risk_factors,
                        culturalFit: e.evaluationResult?.cultural_fit,
                        interviewFocus: e.evaluationResult?.interview_focus,
                    })),
                }));

                setVersions(transformedVersions);

                // If no version is selected yet, or the current selected version is not in the new list,
                // set the active version as the selection
                const currentSelectedExists = transformedVersions.some(v => v.id === selectedVersionId);
                if (!selectedVersionId || !currentSelectedExists) {
                    const activeVersion = transformedVersions.find(v => v.isActive);
                    setSelectedVersionId(activeVersion?.id || transformedVersions[0]?.id || '');
                }
            } else {
                toast.error('Failed to load prompt versions');
            }
        } catch (error) {
            console.error('Error fetching prompt versions:', error);
            toast.error('Failed to load prompt versions');
        } finally {
            setIsLoading(false);
        }
    };

    // Load versions on mount
    useEffect(() => {
        if (open) {
            fetchVersions();
        }
    }, [open, job.jobId]);

    const handleSetAsActive = async (versionId: string) => {
        setIsLoading(true);
        try {
            const response = await aiMatchingService.updateActivePromptVersion({
                expertId,
                jobId: job.jobId,
                versionId,
            });

            if (response.apiStatus === 'SUCCESS') {
                toast.success('Active version updated successfully');

                // Refresh versions to update the active status
                const updatedVersions = versions.map(v => ({
                    ...v,
                    isActive: v.id === versionId,
                }));
                setVersions(updatedVersions);
            } else {
                toast.error('Failed to update active version');
            }
        } catch (error) {
            console.error('Error updating active version:', error);
            toast.error('Failed to update active version');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteVersion = (versionId: string) => {
        setVersionToDelete(versionId);
    };

    const executeDeleteVersion = async () => {
        if (!versionToDelete) return;

        setIsLoading(true);
        try {
            const response = await aiMatchingService.deleteDraftPromptVersion({
                expertId,
                jobId: job.jobId,
                versionId: versionToDelete,
            });

            if (response.apiStatus === 'SUCCESS') {
                toast.success('Draft deleted successfully');
                await fetchVersions();

                if (selectedVersionId === versionToDelete) {
                    const activeVersion = versions.find(v => v.isActive);
                    setSelectedVersionId(activeVersion?.id || '');
                }
            } else {
                toast.error('Failed to delete draft');
            }
        } catch (error) {
            console.error('Error deleting draft:', error);
            toast.error('Failed to delete draft');
        } finally {
            setIsLoading(false);
            setVersionToDelete(null);
        }
    };

    const handleRegeneratePrompt = async (experimentName: string, modificationRequest: string, onSuccess?: () => void) => {
        if (!modificationRequest.trim()) {
            toast.error('Please provide modification instructions');
            return;
        }

        setIsRegenerating(true);
        setIsLoading(true);

        try {
            const selectedVersion = versions.find(v => v.id === selectedVersionId);
            if (!selectedVersion) {
                toast.error('No version selected');
                setIsRegenerating(false);
                setIsLoading(false);
                return;
            }

            const initialPayload = {
                expertId,
                jobId: job.jobId,
                prompt: selectedVersion.prompt,
                changelog: modificationRequest,
                status: 'DRAFT' as const,
                experimentName: experimentName || `Experiment ${new Date().toLocaleString()}`,
                ...(selectedVersion.status === 'DRAFT'
                    ? {
                        promptVersionId: selectedVersion.id,
                        parentActiveVersionId: selectedVersion.parentActiveVersionId
                    }
                    : {
                        parentActiveVersionId: selectedVersion.id
                    }),
            };

            const response = await aiMatchingService.createPromptVersion({
                ...initialPayload,
                async: true,
            });

            if (response.apiStatus === 'SUCCESS') {
                const poolingId = response.output.poolingId;

                if (response.output.status === 'STARTED' && poolingId) {
                    // Start polling
                    const startTime = Date.now();
                    const timeout = 5 * 60 * 1000; // 5 minutes

                    const poll = async () => {
                        if (Date.now() - startTime > timeout) {
                            toast.error('Prompt regeneration timed out after 5 minutes');
                            setIsRegenerating(false);
                            setIsLoading(false);
                            return;
                        }

                        try {
                            const pollResponse = await aiMatchingService.createPromptVersion({
                                ...initialPayload,
                                async: true,
                                poolingId,
                            });

                            if (pollResponse.apiStatus === 'SUCCESS') {
                                if (pollResponse.output.status === 'STARTED') {
                                    // Still processing, poll again in 5 seconds
                                    setTimeout(poll, 5000);
                                } else {
                                    // Completed!
                                    toast.success('Prompt regenerated successfully');
                                    const newVersionId = pollResponse.output.versionId;
                                    await fetchVersions();
                                    if (newVersionId) {
                                        setSelectedVersionId(newVersionId);
                                    }
                                    onSuccess?.();
                                    setIsRegenerating(false);
                                    setIsLoading(false);
                                }
                            } else {
                                toast.error('Failed to regenerate prompt during polling');
                                setIsRegenerating(false);
                                setIsLoading(false);
                            }
                        } catch (error) {
                            console.error('Error while polling prompt status:', error);
                            toast.error('Error while polling prompt status');
                            setIsRegenerating(false);
                            setIsLoading(false);
                        }
                    };

                    // Initial delay before first poll
                    setTimeout(poll, 5000);
                } else if (response.output.status !== 'STARTED') {
                    // If it finished immediately (though async: true was requested)
                    toast.success('Prompt regenerated successfully');
                    const newVersionId = response.output.versionId;
                    await fetchVersions();
                    if (newVersionId) {
                        setSelectedVersionId(newVersionId);
                    }
                    onSuccess?.();
                    setIsRegenerating(false);
                    setIsLoading(false);
                }
            } else {
                toast.error(response.apiMessage || 'Failed to initiate prompt regeneration');
                setIsRegenerating(false);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error initiating prompt regeneration:', error);
            toast.error('Failed to initiate prompt regeneration');
            setIsRegenerating(false);
            setIsLoading(false);
        }
    };

    const handleApplyPromptToAll = (versionId: string) => {
        setApplyToAllVersionId(versionId);
    };

    const executeApplyPromptToAll = async () => {
        if (!applyToAllVersionId) return;

        setIsLoading(true);
        try {
            const response = await aiMatchingService.applyPromptVersionToAll({
                expertId,
                jobId: job.jobId,
                promptVersionId: applyToAllVersionId,
            }) as ApplyPromptToAllResponse;

            if (response.apiStatus === 'SUCCESS') {
                if (response.output.status === 'QUEUED') {
                    toast.success('Prompt version applied to all candidates successfully');
                    onClose(); // Close drawer on success (QUEUED)
                } else if (response.output.status === 'PROCESSING') {
                    toast.warning('Existing evaluation process still in progress, try after some time');
                }
            } else {
                toast.error(response.apiMessage || 'Failed to apply prompt version');
            }
        } catch (error) {
            console.error('Error applying prompt version:', error);
            toast.error('Failed to apply prompt version');
        } finally {
            setIsLoading(false);
            setApplyToAllVersionId(null);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: TestingStep) => {
        setActiveTab(newValue);
    };

    const handleVersionSelect = (versionId: string) => {
        setSelectedVersionId(versionId);
        setDraftPrompt(''); // Clear draft when switching versions
    };

    const handleDraftPromptChange = (prompt: string) => {
        setDraftPrompt(prompt);
    };

    const selectedVersion = versions.find(v => v.id === selectedVersionId);
    const hasPrompt = !!(draftPrompt || selectedVersion?.prompt?.userTemplate);
    const isDraft = selectedVersion?.status === 'DRAFT';

    const displayPrompt = draftPrompt
        ? { system: selectedVersion?.prompt?.system || '', userTemplate: draftPrompt }
        : (selectedVersion?.prompt || { system: '', userTemplate: '' });

    // Auto-redirect to Refine tab if current version is not a draft and we're on a restricted tab
    useEffect(() => {
        if (!isDraft && activeTab !== 'refine') {
            setActiveTab('refine');
        }
    }, [isDraft, activeTab]);

    const { talentPoolCandidates, coachClientEvaluationsCandidates } = useJobPipeline();

    // Determine candidates to show in Testing tab
    // Rule: Talent pool takes priority, otherwise use coach/client evaluations
    const availableCandidates = talentPoolCandidates.length > 0
        ? talentPoolCandidates
        : coachClientEvaluationsCandidates;

    return (
        <>
            <OverlayLoader loading={isLoading} />
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '85vh',
                        maxHeight: '85vh',
                    },
                }}
            >
                <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="h6">AI Matching Settings</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Configure and test AI prompts for {job.jobTitle}
                                {selectedVersion && (
                                    <>
                                        {' • '}
                                        <Typography component="span" color={selectedVersion.status === 'DRAFT' ? 'primary.main' : 'text.primary'} fontWeight={500}>
                                            {selectedVersion.status === 'DRAFT'
                                                ? `Editing: ${selectedVersion.experimentName || 'New experiment'}`
                                                : `Viewing: ${selectedVersion.experimentName ? `${selectedVersion.experimentName} - ` : ''}v${selectedVersion.version}${selectedVersion.isActive ? ' (Active)' : ''}`
                                            }
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconButton onClick={onClose} size="small">
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="AI Settings Tabs"
                        sx={{ px: 3 }}
                    >
                        <Tab label="Refine" value="refine" />
                        <Tab label="Preview" value="preview" disabled={!isDraft || !hasPrompt} />
                        <Tab label="Test" value="test" disabled={!isDraft || !hasPrompt} />
                    </Tabs>
                </Box>

                <DialogContent sx={{ p: 3, overflow: 'auto' }}>
                    {activeTab === 'refine' && (
                        <PromptEditor
                            jobId={job.jobId}
                            expertId={expertId}
                            versions={versions}
                            selectedVersionId={selectedVersionId}
                            onVersionSelect={handleVersionSelect}
                            onSetAsActive={handleSetAsActive}
                            onRegeneratePrompt={handleRegeneratePrompt}
                            isRegenerating={isRegenerating}
                            onNavigateToPreview={() => {
                                // Auto-navigate to Preview sub-tab after regeneration
                                // This will be handled by PromptEditor's internal state
                            }}
                            draftPrompt={draftPrompt}
                            onDraftPromptChange={handleDraftPromptChange}
                            onDeleteVersion={handleDeleteVersion}
                            onNavigateToTest={() => setActiveTab('test')}
                        />
                    )}

                    {activeTab === 'preview' && (
                        <PromptPreview prompt={displayPrompt} />
                    )}

                    {activeTab === 'test' && (
                        <PromptTesting
                            jobId={job.jobId}
                            candidates={availableCandidates}
                            onBack={() => setActiveTab('refine')}
                            parentVersionLabel={(() => {
                                const selectedVersion = versions.find(v => v.id === selectedVersionId);
                                const parentVersion = versions.find(v => v.id === selectedVersion?.parentActiveVersionId);
                                if (parentVersion) {
                                    return parentVersion.experimentName || `v${parentVersion.version}`;
                                }
                                const activeVersion = versions.find(v => v.isActive);
                                return activeVersion?.experimentName || (activeVersion?.version ? `v${activeVersion.version}` : 'v1');
                            })()}
                            onApplyToAll={handleApplyPromptToAll}
                            selectedVersionId={selectedVersionId}
                            parentActiveVersionId={versions.find(v => v.id === selectedVersionId)?.parentActiveVersionId}
                            expertId={expertId}
                            setIsLoading={setIsLoading}
                            initialNewResults={versions.find(v => v.id === selectedVersionId)?.evaluations}
                            initialOldResults={versions.find(v => v.id === (versions.find(v => v.id === selectedVersionId)?.parentActiveVersionId))?.evaluations}
                        />
                    )}

                </DialogContent>

                <ConfirmDialog
                    open={!!versionToDelete}
                    onClose={() => setVersionToDelete(null)}
                    onConfirm={executeDeleteVersion}
                    title="Delete Draft Version"
                    message={
                        <>
                            Are you sure you want to delete this draft?
                            <br />
                            This action cannot be undone.
                        </>
                    }
                    confirmText="Delete"
                    confirmColor="error"
                />

                <ConfirmDialog
                    open={!!applyToAllVersionId}
                    onClose={() => setApplyToAllVersionId(null)}
                    onConfirm={executeApplyPromptToAll}
                    title="Apply to All Candidates"
                    message={
                        <>
                            Once you apply to all, all the candidates will be evaluated with this prompt version in background & might take some time.
                        </>
                    }
                    confirmText="Apply to All"
                    confirmColor="primary"
                />
            </Dialog>
        </>
    );
};
